import { useMemo, useState } from 'react';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import {
    useGetVisitasQuery,
    useGetVisitasIndicatorsQuery,
    useDownloadVisitaActaMutation
} from '../../../services/visitasApi';
import { Visita } from '../consts/visitasTypes';
import { useRoles } from '../../../hooks/useRoles';
import { showAlert } from '../../../features/slices/applicationSlice';
import { useTranslation } from 'react-i18next';
import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../../services/presentacionesApi';
import { useSnackbar } from 'notistack';

const useVisitas = ({ filters, isHome }: { filters: any; isHome?: boolean }) => {
    const dispatch = useAppDispatch();
    const { isPreventor } = useRoles();
    const { t } = useTranslation('visitas');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [selected, setSelected] = useState<Visita | null>(null);
    const [openCard, setOpenCard] = useState<boolean>(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerAction, setDrawerAction] = useState<'Detalle' | 'Cancelar' | 'Rechazar' | null>(
        null
    );
    const [selectedVisita, setSelectedVisita] = useState<Visita | null>(null);
    const [downloadVisitaActa] = useDownloadVisitaActaMutation();
    const [getPresentacionRGRLPDFbyId] = useLazyGetPresentacionRGRLPDFbyIdQuery();
    const { enqueueSnackbar } = useSnackbar();

    const filterIsPreventor = useMemo(() => {
        if (!isPreventor) return filters;

        if (isHome && !filters?.idEstado) {
            return { ...filters, estado: 1 };
        }

        return filters;
    }, [filters, isPreventor, isHome]);

    const {
        data: visitasData,
        isLoading,
        refetch: refetchVisitas,
        isFetching
    } = useGetVisitasQuery(filterIsPreventor);

    const {
        data: indicatorsData,
        isFetching: isFetchingIndicators,
        refetch: refetchIndicators
    } = useGetVisitasIndicatorsQuery({});

    const handleClickAction = (event: any, item: Visita) => {
        setSelected(item);
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handleSelectRow = (item: Visita) => {
        setSelected(item);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const handleCloseCard = () => {
        setOpenCard(false);
        setSelected(null);
    };

    const handleShowVisitaData = () => {
        setOpenCard(true);
        setMenuOpen(false);
    };

    const handleOpenDrawer = (action: 'Detalle' | 'Cancelar' | 'Rechazar', visita: Visita) => {
        setDrawerAction(action);
        setSelectedVisita(visita);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setDrawerAction(null);
        setSelectedVisita(null);
    };
    const handleDownloadActa = async () => {
        try {
            if (!selected) return;

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadActa.generating'),
                    loading: true
                })
            );

            const response = await downloadVisitaActa({
                idVisita: selected.idVisita || selected.id,
                idTipoVisita: selected?.idTipoVisita,
                tipoActa: 1
            }).unwrap();

            if (response?.data) {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: 'application/pdf' });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const fileName = `${t('table.actions.downloadActaFilename')}.pdf`;
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });

                enqueueSnackbar(t('alerts.downloadActa.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = t('alerts.downloadActa.error');

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch (parseError) {}

            dispatch(
                showAlert({
                    icon: 'error',
                    message: errorMessage
                })
            );
        }
    };

    const handleDownloadVerosimilitud = async () => {
        try {
            if (!selected) return;

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadVerosimilitud.generating'),
                    loading: true
                })
            );

            const response = await downloadVisitaActa({
                idVisita: selected.idVisita || selected.id,
                idTipoVisita: selected?.idTipoVisita,
                tipoActa: 2
            }).unwrap();

            if (response?.data) {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: 'application/pdf' });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const fileName = `${t('table.actions.downloadVerosimilitudFilename')}.pdf`;
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });

                enqueueSnackbar(t('alerts.downloadVerosimilitud.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = t('alerts.downloadVerosimilitud.error');

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch (parseError) {}

            dispatch(
                showAlert({
                    icon: 'error',
                    message: errorMessage
                })
            );
        }
    };

    const handleDownloadRgrl = async () => {
        try {
            if (!selected || !selected?.idPresentacion) {
                dispatch(
                    showAlert({
                        icon: 'error',
                        message: t('alerts.downloadRgrl.notFoundId')
                    })
                );
                return;
            }

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadRgrl.generating'),
                    loading: true
                })
            );

            const response = await getPresentacionRGRLPDFbyId({
                idPresentacion: selected?.idPresentacion,
                typeId: 2
            }).unwrap();

            if (response?.data?.presignedUrls?.length > 0) {
                const url = response.data.presignedUrls[0];
                const link = document.createElement('a');
                link.href = url;
                const fileName = `${t('table.actions.downloadRgrlFilename')}_${
                    selected?.idPresentacion
                }.pdf`;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });

                enqueueSnackbar(t('alerts.downloadRgrl.success'), { variant: 'success' });
            } else {
                throw new Error('No se recibió el archivo.');
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            dispatch(
                showAlert({
                    icon: 'error',
                    message: error?.message || t('alerts.downloadRgrl.error')
                })
            );
        }
    };

    return {
        selected,
        visitasData,
        indicatorsData,
        isFetchingIndicators,
        openCard,
        anchorEl,
        menuOpen,
        isLoading,
        isFetching,
        handleClickAction,
        handleSelectRow,
        handleCloseMenu,
        handleCloseCard,
        handleShowVisitaData,
        refetchVisitas,
        drawerOpen,
        drawerAction,
        handleOpenDrawer,
        handleCloseDrawer,
        selectedVisita,
        setDrawerOpen,
        refetchIndicators,
        handleDownloadVerosimilitud,
        handleDownloadActa,
        handleDownloadRgrl
    };
};

export default useVisitas;
